import { useState, useMemo, Dispatch, SetStateAction, useEffect } from 'react';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import * as yup from 'yup';
import CalendarCustomHeader from './custom-header';
import CalendarCustomInput from './custom-input';

interface Props {
  deliveryDate: Date | null;
  deliveryKey: string;
  setDeliveryDate: Dispatch<SetStateAction<Date | null>>;
  setIsDateError: Dispatch<SetStateAction<boolean>>;
}

const CalendarCustomDatePicker = ({ deliveryDate, setDeliveryDate, deliveryKey, setIsDateError }: Props) => {
  const MIN_DATE = new Date();
  MIN_DATE.setDate(MIN_DATE.getDate() + 2);
  const MAX_DATE = new Date();
  MAX_DATE.setDate(MAX_DATE.getDate() + 30);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const inputSchema = useMemo(() => {
    return yup.object().shape({
      value: yup.string().required(),
    });
  }, []);

  const isValidInput = () => {
    try {
      const dateObj = { value };
      inputSchema.validateSync(dateObj);
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    if (deliveryKey === 'choosedelivery') {
      const isValid = isValidInput();
      setIsDateError(!isValid);
    }
  }, [deliveryKey, isValidInput]);

  return (
    <DatePicker
      startDate={deliveryDate}
      onChange={(date) => setDeliveryDate(date)}
      onSelect={(date) => setDeliveryDate(date)}
      dateFormat="dd/MM/yyyy"
      selected={deliveryDate}
      filterDate={(date) => {
        const day = date.getDay();
        return day !== 0 && day !== 6;
      }}
      maxDate={MAX_DATE}
      minDate={MIN_DATE}
      shouldCloseOnSelect
      required
      customInput={
        <CalendarCustomInput
          setValue={setValue}
          isValidInput={isValidInput}
          setIsFocused={setIsFocused}
          isFocused={isFocused}
        />
      }
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        ...props
      }: ReactDatePickerCustomHeaderProps) => (
        <CalendarCustomHeader
          date={date}
          changeYear={changeYear}
          changeMonth={changeMonth}
          decreaseMonth={decreaseMonth}
          increaseMonth={increaseMonth}
          prevMonthButtonDisabled={prevMonthButtonDisabled}
          nextMonthButtonDisabled={nextMonthButtonDisabled}
          {...props}
        />
      )}
    />
  );
};

export default CalendarCustomDatePicker;
